import React, { useCallback, useRef, useState, FC, RefObject } from 'react';
import { observer } from 'mobx-react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';

import Icon from '../shared/Icon';
import { hidePicker } from '../shared/Picker';

import IconButton from '../shared/IconButton';
import HeaderMenu from './HeaderMenu';
import Hp from './Hp';
import TopInfo from './TopInfo';
import SettingsModal from '../SettingsModal';
import HelpModal from '../HelpModal';
import LeaderboardModal from '../LeaderboardModal';
import DonationModal from '../DonationModal';
import BonusesModal from '../BonusesModal';
import QuestsModal from '../QuestsModal';
import FriendsModal from '../FriendsModal';
import ClanModal from '../ClanModal';

import appStore from '../../stores/app';
import heroStore from '../../stores/hero';
import bonusesStore from '../../stores/bonuses';
import clansStore from '../../stores/clans';
import buildingData from '../../stores/buildingData';

import { IS_IOS, IS_WEB } from '../../lib/appConstants';
import { DISCORD_LINK } from '../../lib/config';
import { BASE_COLOR, MARGIN_10, MARGIN_15, PADDING1, RED, pOrT } from '../styleConstants';
import { moveToBuilding } from '../BuildingScreen';

import { getBuilding } from '../../utils/buildingUtils';
import { isQuick } from '../../utils/warriorUtils';
import { t, linkTo } from '../../utils/appUtils';

const MENU_ICON_SIZE = pOrT(22, 28);

const styles = StyleSheet.create({
  menuIcon: {
    position: 'absolute',
    top: 6,
    zIndex: 1,
  },
  loading: {
    position: 'absolute',
    right: pOrT(265, 380),
    top: pOrT(2, 8),
  },
  backIcon: {
    position: 'absolute',
    right: pOrT(237, 340),
    top: pOrT(1, 4),
  },
  infoWrapper: {
    flexDirection: 'row',
    marginBottom: PADDING1,
  },
  logo: {
    position: 'absolute',
    left: '50%',
    marginLeft: -60,
    marginTop: -7,
  },
  menuItemsWrapper: {
    flexDirection: 'row',
    position: 'absolute',
    top: pOrT(1, 4),
    right: pOrT(292, 406),
    overflow: 'visible',
  },
});

export const DroppedIconButton: FC<{ style: any; onPress: () => void }> = ({ style, onPress }) => (
  <IconButton
    style={style}
    key="dropped"
    onPress={onPress}
    size={20}
    name="dropped"
    iconStyle={{
      width: MENU_ICON_SIZE,
      height: MENU_ICON_SIZE,
    }}
    source={require('../../assets/images/dropped.png')}
    title={t('combat.dropped')}
  />
);

export const ChatIconButton: FC<{ style: any }> = ({ style }) => (
  <IconButton
    style={style}
    key="chat"
    onPress={() => {
      appStore.toggleChat(true);
    }}
    size={20}
    name="chat"
    iconStyle={{
      width: MENU_ICON_SIZE,
      height: MENU_ICON_SIZE,
    }}
    source={require('../../assets/images/chat.png')}
    title={t('menu.chat')}
  />
);

function onBack(): void {
  const currentNav = appStore.currentNavs.inner;
  const { hero } = heroStore;
  const { building } = hero.location;

  if (currentNav === 'Hero') {
    appStore.navigate('Location');
    return;
  }

  if (currentNav === 'Inventory') {
    appStore.navigate('Hero');
    return;
  }

  if (building) {
    const { parent } = getBuilding(appStore.initData.buildings, building) as any;

    if (moveToBuilding(parent ? parent.id : null) !== false) {
      buildingData.clear();
      hidePicker();
    }

    return;
  }

  appStore.navigate('Hero');
}

interface MenuPos {
  top: number;
  left: number;
}

export default observer(() => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showDonation, setShowDonation] = useState(false);
  const [showBonuses, setShowBonuses] = useState(false);
  const [showQuests, setShowQuests] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [showClan, setShowClan] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const moreRef = useRef<any>(null);
  const currentNav = appStore.currentNavs.inner;

  const toggleMenu = useCallback(() => {
    if (menuVisible) {
      setMenuVisible(false);
      return;
    }
    if (IS_WEB && moreRef.current) {
      moreRef.current.measureInWindow((x: number, y: number, width: number, height: number) => {
        setMenuPos({ top: y + height + 10, left: x - 130 });
        setMenuVisible(true);
      });
    } else {
      setMenuVisible(true);
    }
  }, [menuVisible]);

  return (
    <View style={{ position: 'relative', overflow: 'visible', zIndex: 999 }}>
      <View style={styles.infoWrapper}>
        <View>
          <Hp warrior={heroStore.hero} showQuickSignUp={isQuick(heroStore.hero)} />
        </View>
        <View style={{ flex: 1, alignItems: 'flex-end' }}>
          <TopInfo />
        </View>
      </View>
      {appStore.smallLoading && !appStore.loading ? (
        <ActivityIndicator color={BASE_COLOR} style={styles.loading} />
      ) : null}
      <IconButton
        style={styles.backIcon}
        onPress={onBack}
        size={20}
        name={currentNav === 'Hero' ? 'map' : 'back'}
        title={currentNav === 'Hero' ? t('map') : t('back')}
        source={
          currentNav === 'Hero'
            ? require('../../assets/images/map.png')
            : require('../../assets/images/back.png')
        }
        iconStyle={{ width: MENU_ICON_SIZE, height: MENU_ICON_SIZE }}
      />
      <View style={styles.menuItemsWrapper}>
        <IconButton
          key="hero"
          onPress={() => {
            appStore.navigate('Hero');
            (appStore.toggleMenu as any)(false);
          }}
          size={20}
          name="bag"
          iconStyle={{
            width: MENU_ICON_SIZE,
            height: MENU_ICON_SIZE,
          }}
          source={require('../../assets/images/heroIcon.png')}
          title={t('menu.hero')}
        />
        <IconButton
          style={{ marginLeft: pOrT(MARGIN_10, MARGIN_10) }}
          key="inventory"
          onPress={() => {
            appStore.navigate('Inventory');
            (appStore.toggleMenu as any)(false);
          }}
          size={20}
          name="bag"
          iconStyle={{
            width: MENU_ICON_SIZE,
            height: MENU_ICON_SIZE,
          }}
          source={require('../../assets/images/inventory.png')}
          title={t('menu.inventory')}
        />

        <ChatIconButton style={{ marginLeft: pOrT(MARGIN_10, MARGIN_10) }} />
        <IconButton
          style={{ marginLeft: pOrT(MARGIN_10, MARGIN_10) }}
          key="discord"
          name="discord"
          onPress={() => {
            linkTo(DISCORD_LINK);
          }}
          size={20}
          testID="inner.discord"
          iconStyle={{
            width: MENU_ICON_SIZE,
            height: MENU_ICON_SIZE,
          }}
          source={require('../../assets/images/discord.png')}
          title={t('menu.discord')}
        />
        <View style={{ marginLeft: pOrT(MARGIN_10, MARGIN_10) }}>
          <IconButton
            key="bonuses"
            onPress={() => setShowBonuses(true)}
            size={20}
            name="bonuses"
            iconStyle={{
              width: MENU_ICON_SIZE,
              height: MENU_ICON_SIZE,
            }}
            source={require('../../assets/images/dropped.png')}
            title={t('menu.bonuses')}
            testID="inner.bonuses"
          />
          {bonusesStore.unsettledCount > 0 ? (
            <View
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: RED,
              }}
            />
          ) : null}
        </View>
        <View ref={moreRef} style={{ marginLeft: pOrT(MARGIN_10, MARGIN_10), overflow: 'visible' }}>
          <IconButton
            key="more"
            onPress={toggleMenu}
            size={20}
            name="cog"
            testID="inner.more"
            iconStyle={{
              width: MENU_ICON_SIZE,
              height: MENU_ICON_SIZE,
            }}
            source={require('../../assets/images/more.png')}
            title={t('menu.more')}
          />
          {clansStore.inviteCount > 0 || bonusesStore.claimableQuestCount > 0 ? (
            <View
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: RED,
              }}
            />
          ) : null}
          {menuVisible ? (
            <HeaderMenu
              onClose={() => setMenuVisible(false)}
              menuStyle={IS_WEB && menuPos ? menuPos : { top: 5, right: 0 }}
              items={[
                {
                  key: 'quests',
                  label: t('menu.quests'),
                  source: require('../../assets/images/quests.png'),
                  onPress: () => {
                    setShowQuests(true);
                    setMenuVisible(false);
                  },
                },
                {
                  key: 'leaderboard',
                  label: t('leaderboard.title'),
                  source: require('../../assets/images/leaderboard.png'),
                  onPress: () => {
                    setShowLeaderboard(true);
                    setMenuVisible(false);
                  },
                },
                {
                  key: 'friends',
                  label: t('menu.friends'),
                  source: require('../../assets/images/friends.png'),
                  onPress: () => {
                    setShowFriends(true);
                    setMenuVisible(false);
                  },
                },
                {
                  key: 'clan',
                  label: t('menu.clan'),
                  source: require('../../assets/images/clan.png'),
                  onPress: () => {
                    setShowClan(true);
                    setMenuVisible(false);
                  },
                },
                {
                  key: 'help',
                  label: t('help.help'),
                  source: require('../../assets/images/help.png'),
                  onPress: () => {
                    setShowHelp(true);
                    setMenuVisible(false);
                  },
                },
                ...(IS_IOS
                  ? []
                  : [
                      {
                        key: 'donation',
                        label: t('donation.title'),
                        source: require('../../assets/images/donation.png'),
                        onPress: () => {
                          setShowDonation(true);
                          setMenuVisible(false);
                        },
                      },
                    ]),
                {
                  key: 'settings',
                  label: t('menu.settings'),
                  source: require('../../assets/images/settings.png'),
                  onPress: () => {
                    setShowSettings(true);
                    setMenuVisible(false);
                  },
                },
              ]}
            />
          ) : null}
        </View>
      </View>

      {showSettings ? <SettingsModal onHide={() => setShowSettings(false)} /> : null}
      {showHelp ? <HelpModal onHide={() => setShowHelp(false)} /> : null}
      {showLeaderboard ? <LeaderboardModal onHide={() => setShowLeaderboard(false)} /> : null}
      {showDonation ? <DonationModal onHide={() => setShowDonation(false)} /> : null}
      {showBonuses ? <BonusesModal onHide={() => setShowBonuses(false)} /> : null}
      {showQuests ? <QuestsModal onHide={() => setShowQuests(false)} /> : null}
      {showFriends ? <FriendsModal onHide={() => setShowFriends(false)} /> : null}
      {showClan ? <ClanModal onHide={() => setShowClan(false)} /> : null}
    </View>
  );
});
